import React, { useEffect, useRef, memo, useCallback, useState, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Heart, MapPin, FileCheck, Church, Send, ScrollText, Camera, Star, Shield, Globe, CheckCircle, Lock, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { holySites, testimonials, blogArticles, faqs } from '../mockData';
import SEO from '../components/SEO';
import { holySitePageBySiteId } from '../data/holySitePages';
import { homeFaqSchema } from '../data/homeFaqSchema';
import { useFaqSchema } from '../hooks/useFaqSchema';
import { useLanguage } from '../context/LanguageContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
// import DeliveryProofCarousel from '../components/DeliveryProofCarousel'; // Kept on disk — re-enable when ready

// Optimized Image component with lazy loading, blur placeholder, and mobile optimization
const OptimizedImage = memo(({ src, alt, className, priority = false, mobileSrc }) => {
  const [loaded, setLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);
  
  // Use smaller image on mobile if provided
  const imageSrc = isMobile && mobileSrc ? mobileSrc : src;
  
  return (
    <img 
      src={imageSrc} 
      alt={alt}
      className={`${className} transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={priority ? "high" : "low"}
      onLoad={() => setLoaded(true)}
    />
  );
});

// Live Prayer Counter - simulates real-time activity
// Shared counter state - all LivePrayerCounter instances show the same number
const sharedCounter = (() => {
  const now = new Date();
  const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000);
  const hour = now.getHours();
  const hourBoost = hour >= 8 && hour <= 22 ? 15 : 5;
  const base = 24 + (dayOfYear % 19) + hourBoost;
  let current = base;
  const listeners = new Set();
  
  setInterval(() => {
    const change = Math.random();
    if (change > 0.7) current += 1;
    else if (change < 0.15 && current > base - 3) current -= 1;
    listeners.forEach(fn => fn(current));
  }, 8000 + Math.random() * 12000);
  
  return { get: () => current, subscribe: (fn) => { listeners.add(fn); return () => listeners.delete(fn); } };
})();

const LivePrayerCounter = memo(() => {
  const [count, setCount] = useState(sharedCounter.get);
  
  useEffect(() => sharedCounter.subscribe(setCount), []);
  
  return (
    <span>{count}</span>
  );
});

// Verse of the Day - rotates daily
const VERSES = [
  { text: "Now faith is confidence in what we hope for and assurance about what we do not see.", ref: "Hebrews 11:1" },
  { text: "For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, plans to give you hope and a future.", ref: "Jeremiah 29:11" },
  { text: "The Lord is my shepherd; I shall not want.", ref: "Psalm 23:1" },
  { text: "Be strong and courageous. Do not be afraid; do not be discouraged, for the Lord your God will be with you wherever you go.", ref: "Joshua 1:9" },
  { text: "Trust in the Lord with all your heart and lean not on your own understanding.", ref: "Proverbs 3:5" },
  { text: "I can do all this through him who gives me strength.", ref: "Philippians 4:13" },
  { text: "Cast all your anxiety on him because he cares for you.", ref: "1 Peter 5:7" },
];

const VerseOfTheDay = memo(() => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000);
  const verse = VERSES[dayOfYear % VERSES.length];
  
  return (
    <>
      <p className="text-xl md:text-2xl text-gray-800 italic font-serif leading-relaxed mb-3">
        "{verse.text}"
      </p>
      <p className="text-base font-semibold text-amber-700">{verse.ref}</p>
    </>
  );
});


// Memoized components for better performance
const HolySiteCard = memo(({ site }) => (
  <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300 group">
    <div className="relative h-48 overflow-hidden">
      <OptimizedImage 
        src={site.imageUrl} 
        alt={site.name}
        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      <div className="absolute bottom-4 left-4 right-4">
        <h3 className="text-white font-semibold text-lg">{site.name}</h3>
      </div>
    </div>
  </Card>
));

const TestimonialCard = memo(({ testimonial }) => (
  <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow duration-300">
    <CardContent className="p-6">
      <div className="flex items-center mb-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white font-bold text-lg">
          {testimonial.name.charAt(0)}
        </div>
        <div className="ml-4">
          <p className="font-semibold text-gray-900">{testimonial.name}</p>
          <p className="text-sm text-gray-500">{testimonial.location}</p>
        </div>
      </div>
      <p className="text-gray-600 italic">"{testimonial.text}"</p>
    </CardContent>
  </Card>
));

const Home = () => {
  useFaqSchema('home', homeFaqSchema);
  const heroRef = useRef(null);
  const { t } = useLanguage();
  const [reviews, setReviews] = useState([]);

  // Load real, admin-approved reviews from the backend. If none exist, the
  // testimonials section auto-hides (no more fake fillers).
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    if (!API_URL) return;
    fetch(`${API_URL}/api/reviews/public?limit=8`)
      .then((r) => (r.ok ? r.json() : { reviews: [] }))
      .then((d) => setReviews(Array.isArray(d.reviews) ? d.reviews : []))
      .catch(() => setReviews([]));
  }, []);

  // Optimized intersection observer with useCallback
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '50px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
          // Unobserve after animation to improve performance
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Use requestAnimationFrame for smoother initial render
    requestAnimationFrame(() => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      elements.forEach((el) => observer.observe(el));
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden">
      <SEO 
        title="Online Prayer to the Holy Land"
        description="Send an online prayer to the Holy Land. We print your prayer, hand-deliver it to the Western Wall, Church of the Holy Sepulchre or Garden Tomb in Jerusalem, and email you a dated photograph."
        path="/"
      />
      {/* Hero Section - Full viewport on mobile */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image - crisp, no effects */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/hero-bg.jpg)',
          }}
        />
        {/* Simple dark overlay */}
        <div className="absolute inset-0 bg-black/50" />
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-5 pt-32 md:pt-40 lg:pt-44">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 md:mb-10 inline-block animate-on-scroll">
              <span className="inline-flex items-center gap-1.5 md:gap-2 px-4 md:px-6 py-2 md:py-2.5 bg-gray-800/60 border border-gray-600/50 rounded-full text-white text-xs md:text-sm font-medium backdrop-blur-sm">
                <span className="relative flex h-2 w-2 md:h-2.5 md:w-2.5 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 md:h-2.5 md:w-2.5 bg-green-400"></span>
                </span>
                <span className="hidden md:inline">Personally delivered to Jerusalem — every week</span>
                <span className="md:hidden">Delivered weekly from Jerusalem</span>
              </span>
            </div>
            
            {/* Mobile: 3 lines, Desktop: 2 lines */}
            <h1 className="text-white mb-5 md:mb-8 animate-on-scroll font-serif font-bold tracking-tight" style={{ lineHeight: '1.15' }}>
              {/* Mobile version - 2 balanced lines */}
              <span className="block md:hidden text-[2.4rem]">Send Your Prayer</span>
              <span className="block md:hidden text-[2.4rem]">to the <span className="text-amber-400 italic">Holy Land</span></span>
              
              {/* Desktop version - 2 lines */}
              <span className="hidden md:block text-5xl lg:text-6xl mb-3">Send Your Prayer to the</span>
              
              <span className="hidden md:block md:text-5xl lg:text-6xl text-amber-400 italic">Holy Land</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white mb-7 md:mb-10 max-w-2xl mx-auto leading-relaxed animate-on-scroll" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}>
              Online prayer to the Holy Land, delivered by hand. We print your prayer, carry it to Jerusalem's holiest sites and send you a dated photograph.
            </p>
            
            <div className="flex flex-col gap-3 md:flex-row md:gap-4 justify-center items-center mb-4 md:mb-6 animate-on-scroll px-4">
              <Link to="/prayer-request" className="w-full md:w-auto">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-6 md:px-8 py-5 md:py-6 text-base md:text-lg shadow-xl hover:shadow-2xl transition-all w-full md:w-auto font-semibold">
                  <Send className="mr-2" size={18} />
                  Send Your Prayer to Jerusalem
                </Button>
              </Link>
              <Link to="/sacred-sites" className="w-full md:w-auto">
                <Button size="lg" variant="outline" className="bg-transparent border-2 border-white/70 text-white hover:bg-white hover:text-gray-900 px-6 md:px-8 py-5 md:py-6 text-base md:text-lg transition-all w-full md:w-auto font-semibold">
                  <MapPin className="mr-2" size={18} />
                  Discover Sacred Sites
                </Button>
              </Link>
            </div>

            {/* Photo proof promise */}
            <div data-testid="money-back-guarantee" className="mb-4 md:mb-6 animate-on-scroll text-center">
              <span className="text-sm text-white/80 font-medium"><span className="text-green-400 mr-1">✓</span> Includes a commemorative photograph of your scroll in Jerusalem</span>
            </div>

            {/* Emergency rush ribbon — subtle but visible, for cold traffic in crisis */}
            <div className="mb-6 md:mb-8 animate-on-scroll flex justify-center">
              <Link
                to="/urgent"
                data-testid="hero-urgent-ribbon"
                className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600/95 hover:bg-red-600 text-white text-xs md:text-sm font-semibold shadow-lg backdrop-blur-sm transition-all hover:scale-[1.03] border border-red-400/40"
              >
                <span className="relative flex h-2 w-2 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-300 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-300"></span>
                </span>
                <span>Need it urgent? Next-business-day rush delivery</span>
                <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
              </Link>
            </div>
            
            {/* Prayer categories */}
            <div className="flex flex-row justify-center gap-2 md:gap-6 max-w-md md:max-w-3xl mx-auto pb-20 md:pb-24 animate-on-scroll px-2">
              <Link to="/prayer-request?category=healing" className="block flex-1">
                <div className="bg-white/10 border border-white/20 rounded-xl p-3 md:p-6 text-white hover:bg-white/20 transition-all cursor-pointer">
                  <Heart className="w-5 h-5 md:w-8 md:h-8 mb-1 md:mb-3 text-amber-400 mx-auto" />
                  <div className="text-[10px] md:text-sm font-medium text-white/70 hidden md:block">{t('home.prayerFor')}</div>
                  <div className="text-sm md:text-lg font-bold">Healing</div>
                </div>
              </Link>
              <Link to="/prayer-request?category=guidance" className="block flex-1">
                <div className="bg-white/10 border border-white/20 rounded-xl p-3 md:p-6 text-white hover:bg-white/20 transition-all cursor-pointer">
                  <Heart className="w-5 h-5 md:w-8 md:h-8 mb-1 md:mb-3 text-amber-400 mx-auto" />
                  <div className="text-[10px] md:text-sm font-medium text-white/70 hidden md:block">{t('home.prayerFor')}</div>
                  <div className="text-sm md:text-lg font-bold">Guidance</div>
                </div>
              </Link>
              <Link to="/prayer-request?category=blessing" className="block flex-1">
                <div className="bg-white/10 border border-white/20 rounded-xl p-3 md:p-6 text-white hover:bg-white/20 transition-all cursor-pointer">
                  <Heart className="w-5 h-5 md:w-8 md:h-8 mb-1 md:mb-3 text-amber-400 mx-auto" />
                  <div className="text-[10px] md:text-sm font-medium text-white/70 hidden md:block">{t('home.prayerFor')}</div>
                  <div className="text-sm md:text-lg font-bold">Blessing</div>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator - positioned at very bottom */}
        <div className="absolute bottom-3 left-0 right-0 flex justify-center animate-bounce">
          <ChevronRight className="text-white/70 rotate-90" size={24} />
        </div>
      </section>

      {/* Pricing strip removed — moved to Prayer Request page only */}

      {/* Trust Numbers Bar */}
      <section data-testid="trust-numbers-bar" className="py-5 md:py-10 bg-gradient-to-b from-stone-50 to-white border-y border-amber-200/50">
        <div className="container mx-auto px-5">
          <p className="text-center text-xs md:text-sm text-amber-700 font-semibold uppercase tracking-[0.2em] mb-3 md:mb-4">A Real Delivery Service from Jerusalem</p>
          <div className="max-w-3xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
            <div className="text-center">
              <div className="text-xl md:text-3xl font-bold text-gray-900 font-serif">Hand<span className="text-amber-600">-</span>Printed</div>
              <div className="text-[10px] md:text-sm text-gray-500 mt-1">On a Real Scroll</div>
            </div>
            <div className="text-center">
              <div className="text-xl md:text-3xl font-bold text-gray-900 font-serif">Photo<span className="text-amber-600">.</span></div>
              <div className="text-[10px] md:text-sm text-gray-500 mt-1">Proof of Delivery</div>
            </div>
            <div className="text-center">
              <div className="text-xl md:text-3xl font-bold text-gray-900 font-serif">5<span className="text-amber-600">+</span></div>
              <div className="text-[10px] md:text-sm text-gray-500 mt-1">Holy Sites in Jerusalem</div>
            </div>
            <div data-testid="weekly-delivery-stat" className="text-center">
              <div className="text-xl md:text-3xl font-bold text-gray-900 font-serif flex items-center justify-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span>Weekly</span>
              </div>
              <div className="text-[10px] md:text-sm text-gray-500 mt-1">Personal Delivery</div>
            </div>
          </div>
        </div>
      </section>

      {/* Real photo proof carousel — temporarily hidden. To re-enable: uncomment the import at the top and the <DeliveryProofCarousel /> here. */}
      {/* <DeliveryProofCarousel /> */}

      {/* Martin Luther Quote Section - Orange Banner */}
      <section className="py-8 md:py-16 bg-gradient-to-r from-amber-500 to-amber-600">
        <div className="container mx-auto px-5">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg md:text-2xl text-white italic font-medium leading-relaxed mb-3">
              "To be a Christian without prayer is no more possible than to be alive without breathing"
            </p>
            <p className="text-white/90 text-base font-semibold tracking-wide">
              – Martin Luther
            </p>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-10 md:py-16 bg-white">
        <div className="container mx-auto px-5">
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-8 lg:gap-14">
            {/* Video */}
            <div className="w-full lg:w-3/5 rounded-2xl overflow-hidden shadow-2xl relative">
              <video
                data-testid="promo-video"
                className="w-full aspect-video"
                controls
                controlsList="nodownload"
                disablePictureInPicture
                playsInline
                webkit-playsinline="true"
                poster="/video_poster.jpg"
                preload="metadata"
                onContextMenu={(e) => e.preventDefault()}
              >
                <source src="/jerusalem_holy_prayer_video.mp4" type="video/mp4" />
              </video>
            </div>
            {/* Text */}
            <div className="w-full lg:w-2/5 text-center lg:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 font-serif">
                See How Your Prayer Reaches the Holy Land
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-6 leading-relaxed">
                Every week, our messengers in Jerusalem print your prayers on a sacred scroll and personally deliver them to the holiest sites. Watch how your intentions travel from your heart to the Holy Land.
              </p>
              <Link to="/prayer-request">
                <Button data-testid="video-cta-button" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-full text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
                  Send Your Prayer Now
                  <ChevronRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="pt-16 pb-10 md:pt-24 md:pb-16 bg-white">
        <div className="container mx-auto px-5">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 md:mb-10 animate-on-scroll">
              <h2 className="text-2xl md:text-5xl font-bold text-gray-900 mb-3 md:mb-4 font-serif">
                How Your Prayers Are Delivered
              </h2>
              <p className="text-base md:text-xl text-gray-600">Submit your online prayer request — a sacred journey from your heart to the Holy Land</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
              {[
                {
                  step: '1',
                  title: 'Write Your Prayer',
                  description: 'Share your heartfelt intentions in your own words. Your prayer is kept completely private and treated with reverence.',
                  icon: Heart
                },
                {
                  step: '2',
                  title: 'Printed on a Scroll',
                  description: 'Your prayer is printed on a scroll and personally carried to each holy site you choose by our Jerusalem messengers.',
                  icon: ScrollText
                },
                {
                  step: '3',
                  title: 'Receive Photo Proof',
                  description: 'Get timestamped photos of your prayer at the holy site, confirming delivery.',
                  icon: Camera
                }
              ].map((item, index) => (
                <Card 
                  key={index} 
                  className="bg-white border-2 border-gray-100 rounded-2xl shadow-lg hover:shadow-xl hover:border-amber-400 transition-all duration-300 animate-on-scroll"
                >
                  <CardContent className="p-6 md:p-8 text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 md:mb-6 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white text-2xl md:text-3xl font-bold shadow-lg">
                      {item.step}
                    </div>
                    <item.icon className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 md:mb-4 text-amber-600" strokeWidth={1.5} />
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3 font-serif">{item.title}</h3>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* CTA after How It Works */}
            <div className="text-center mt-8 md:mt-16 animate-on-scroll">
              <Link to="/prayer-request">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 md:px-10 py-5 md:py-5 text-base md:text-lg font-semibold shadow-lg hover:shadow-xl transition-all">
                  <Send className="mr-2" size={18} />
                  Submit My Prayer Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Holy Sites Preview */}
      <section className="pt-16 pb-10 md:pt-24 md:pb-16 bg-gray-50">
        <div className="container mx-auto px-5">
          <div className="text-center mb-8 md:mb-10 animate-on-scroll">
            <h2 className="text-2xl md:text-5xl font-bold text-gray-900 mb-3 md:mb-4 font-serif">
              Sacred Destinations Await Your Prayers
            </h2>
            <p className="text-base md:text-xl text-gray-600">Join a spiritual tradition connecting hearts across the world to Jerusalem</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8 max-w-7xl mx-auto">
            {holySites.slice(0, 6).map((site) => (
              <Card key={site.id} className="overflow-hidden group hover:shadow-2xl transition-all transform hover:scale-[1.02] animate-on-scroll border border-gray-200">
                <div className="relative h-48 md:h-64 overflow-hidden">
                  <img
                    src={site.image}
                    alt={site.name}
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-3 md:bottom-4 left-3 md:left-4 right-3 md:right-4">
                    <h3 className="text-lg md:text-xl font-bold text-white mb-1 font-serif">{site.name}</h3>
                    <p className="text-amber-400 text-sm font-medium">{site.location}</p>
                  </div>
                </div>
                <CardContent className="p-4 md:p-6">
                  <p className="text-sm md:text-base text-gray-600 mb-4">{site.description}</p>
                  <Link to="/prayer-request">
                    <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white py-2.5 md:py-3 font-semibold text-sm md:text-base">
                      <Send className="mr-2" size={14} />
                      Deliver My Prayer Here
                    </Button>
                  </Link>
                  {(site.id === 1 || holySitePageBySiteId[site.id]) && (
                    <Link
                      to={site.id === 1 ? '/pray-at-the-church-of-the-holy-sepulchre' : holySitePageBySiteId[site.id]}
                      className="block mt-2 text-center text-xs md:text-sm font-semibold text-amber-700 hover:text-amber-800 underline underline-offset-2"
                      data-testid={site.id === 1 ? 'home-sepulchre-learn-more' : `home-site-learn-more-${site.id}`}
                    >
                      Learn about the {site.id === 1 ? 'Holy Sepulchre' : site.name.replace(/^The /, '')} →
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-6 md:mt-8 animate-on-scroll">
            <Link to="/sacred-sites">
              <Button variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50 font-semibold">
                View All Holy Sites
                <ChevronRight className="ml-1" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section — uses hardcoded mockData testimonials for now.
          When real reviews are collected via /leave-a-review or admin bulk-import,
          swap this back to reading from the `reviews` state (fetched from API). */}
      <section className="py-10 md:py-16 bg-gray-50">
        <div className="container mx-auto px-5">
          <div className="text-center mb-8 md:mb-10 animate-on-scroll">
            <h2 className="text-2xl md:text-5xl font-bold text-gray-900 mb-3 md:mb-4 font-serif">
              Stories From Our Community
            </h2>
            <p className="text-base md:text-xl text-gray-600">Real people, real prayers, real connections to the Holy Land</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8 max-w-5xl mx-auto">
            {testimonials.slice(0, 4).map((testimonial) => (
              <Card key={testimonial.id} className="bg-white border border-gray-200 hover:shadow-xl transition-all animate-on-scroll">
                <CardContent className="p-5 md:p-8">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm md:text-base text-gray-700 mb-5 italic leading-relaxed">"{testimonial.message}"</p>
                  <div className="flex items-center gap-3">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      loading="lazy"
                      className="w-12 h-12 rounded-full object-cover border-2 border-amber-200"
                    />
                    <div>
                      <p className="font-semibold text-gray-900 text-base">{testimonial.name}</p>
                      <p className="text-gray-500 text-sm">{testimonial.location}</p>
                    </div>
                    <div className="ml-auto">
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">{testimonial.site}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-6 md:mt-8 animate-on-scroll">
            <Link to="/testimonials">
              <Button variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50 font-semibold">
                Read More Stories
                <ChevronRight className="ml-1" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section - continuous flow */}
      <section className="py-10 md:py-16 bg-white">
        <div className="container mx-auto px-5">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8 md:mb-10 animate-on-scroll">
              <h2 className="text-2xl md:text-5xl font-bold text-gray-900 mb-3 md:mb-4 font-serif">
                Common Questions
              </h2>
              <p className="text-base md:text-xl text-gray-600">
                Everything you need to know
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-3 md:space-y-4 animate-on-scroll">
              {faqs.filter(faq => [1, 2, 5, 12].includes(faq.id)).map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-gray-50 border-0 rounded-xl shadow-sm px-4 md:px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-amber-600 text-base md:text-lg py-4 md:py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-gray-600 pb-4 md:pb-5 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-6 md:mt-8 animate-on-scroll">
              <Link to="/faq">
                <Button variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50 font-semibold">
                  View All Questions
                  <ChevronRight className="ml-1" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Wisdom & Inspiration section moved to /blog page — kept off the home to reduce funnel distraction */}

      {/* Verse of the Day moved to /holy-word page — same reason */}

      {/* Trust Badges */}
      <section data-testid="trust-badges" className="py-8 md:py-12 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-5">
          <div className="max-w-5xl mx-auto grid grid-cols-3 md:grid-cols-6 gap-4 md:gap-6">
            <div className="text-center animate-on-scroll">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-green-100 flex items-center justify-center">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <p className="font-semibold text-gray-900 text-xs md:text-sm">SSL Secure</p>
              <p className="text-[10px] md:text-xs text-gray-500">256-bit encryption</p>
            </div>
            <div className="text-center animate-on-scroll">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-amber-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-amber-600" />
              </div>
              <p className="font-semibold text-gray-900 text-xs md:text-sm">100% Satisfaction</p>
              <p className="text-[10px] md:text-xs text-gray-500">Money-back guarantee</p>
            </div>
            <div className="text-center animate-on-scroll">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-yellow-100 flex items-center justify-center">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
              <p className="font-semibold text-gray-900 text-xs md:text-sm">5-Star Reviews</p>
              <p className="text-[10px] md:text-xs text-gray-500">Trusted worldwide</p>
            </div>
            <div className="text-center animate-on-scroll">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-purple-100 flex items-center justify-center">
                <Camera className="w-6 h-6 text-purple-600" />
              </div>
              <p className="font-semibold text-gray-900 text-xs md:text-sm">Verified Photos</p>
              <p className="text-[10px] md:text-xs text-gray-500">Every delivery proven</p>
            </div>
            <div className="text-center animate-on-scroll">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-blue-100 flex items-center justify-center">
                <Lock className="w-6 h-6 text-blue-600" />
              </div>
              <p className="font-semibold text-gray-900 text-xs md:text-sm">Secure Payment</p>
              <p className="text-[10px] md:text-xs text-gray-500">Powered by Stripe</p>
            </div>
            <div className="text-center animate-on-scroll">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-blue-100 flex items-center justify-center">
                <FileCheck className="w-6 h-6 text-blue-600" />
              </div>
              <p className="font-semibold text-gray-900 text-xs md:text-sm">Verified Service</p>
              <p className="text-[10px] md:text-xs text-gray-500">Since 2024</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 md:py-20 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(/hero-bg.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>
        <div className="container mx-auto px-5 relative z-10">
          <div className="max-w-3xl mx-auto text-center animate-on-scroll">
            <p className="text-lg md:text-2xl text-amber-400 italic font-medium mb-4 md:mb-6 leading-relaxed">
              "Prayer is not conquering God's reluctance, but taking hold of God's willingness"
            </p>
            <p className="text-gray-400 text-base font-medium mb-6 md:mb-10 tracking-wide">– Phillips Brooks</p>
            <p className="text-base md:text-lg text-gray-300 mb-8 md:mb-10 leading-relaxed">
              Join countless faithful souls who have discovered profound peace through this spiritual connection to the land where faith was born.
            </p>
            <Link to="/prayer-request">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 md:px-12 py-5 md:py-6 text-base md:text-xl font-semibold shadow-xl hover:shadow-2xl transition-all">
                <Send className="mr-2 md:mr-3" size={20} />
                Begin Your Sacred Journey
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;